import React, { useState } from 'react';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from 'app/components/ui/card';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import { Alert, AlertDescription } from 'app/components/ui/alert';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from 'app/components/ui/tabs';
import {
    Package,
    AlertTriangle,
    TrendingUp,
    TrendingDown,
    Plus,
    Minus,
    BarChart3,
    Bell,
    Eye,
    Edit,
} from 'lucide-react';
import { useInventorySlice } from './slice';
import { InventoryOverviewProps } from 'types/inventory';
import InventoryAdjustmentDialog from './components/InventoryAdjustmentDialog';
import BulkAdjustmentDialog from './components/BulkAdjustmentDialog';
import InventoryReports from './components/InventoryReports';
import ProductInventoryDetails from './components/ProductInventoryDetails';

const InventoryManagement: React.FC<InventoryOverviewProps> = ({
    className,
}) => {
    const [selectedProductId, setSelectedProductId] = useState<number | null>(
        null,
    );
    const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(false);
    const [showBulkAdjustmentDialog, setShowBulkAdjustmentDialog] =
        useState(false);
    const [adjustmentProductId, setAdjustmentProductId] = useState<number | null>(
        null,
    );

    // Use the inventory slice to inject reducers and get hooks
    const { useGetInventoryOverviewQuery, useGetInventoryAlertsQuery } =
        useInventorySlice();

    const {
        data: overview,
        isLoading: overviewLoading,
        error: overviewError,
    } = useGetInventoryOverviewQuery({});
    const {
        data: alerts,
        isLoading: alertsLoading,
        error: alertsError,
    } = useGetInventoryAlertsQuery({ threshold: 10 });

    const handleAdjustInventory = (productId: number) => {
        setAdjustmentProductId(productId);
        setShowAdjustmentDialog(true);
    };

    const handleBulkAdjustment = () => {
        setShowBulkAdjustmentDialog(true);
    };

    const handleViewProductDetails = (productId: number) => {
        setSelectedProductId(productId);
    };

    if (overviewLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    if (overviewError) {
        return (
            <div className={`space-y-6 ${className}`}>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">
                            Inventory Management
                        </h1>
                        <p className="text-muted-foreground">
                            Monitor and manage your product inventory
                        </p>
                    </div>
                </div>

                <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        <div className="space-y-2">
                            <p className="font-semibold">API Server Not Available</p>
                            <p>
                                The inventory API server is not running on port 3005. To see
                                inventory data, please start the backend server.
                            </p>
                            <p className="text-sm text-muted-foreground">
                                Expected API endpoint:
                                http://localhost:3005/api/v1/admin/inventory/overview
                            </p>
                        </div>
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    const stats = overview?.statistics;

    return (
        <div className={`space-y-6 ${className}`}>
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">
                        Inventory Management
                    </h1>
                    <p className="text-muted-foreground">
                        Monitor and manage your product inventory
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={handleBulkAdjustment} variant="outline">
                        <Plus className="h-4 w-4 mr-2" />
                        Bulk Adjustment
                    </Button>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Total Products
                        </CardTitle>
                        <Package className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {stats?.total_products || 0}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {stats?.active_products || 0} active products
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Stock</CardTitle>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{stats?.total_stock || 0}</div>
                        <p className="text-xs text-muted-foreground">
                            Avg: {stats?.avg_stock_per_product || 0} per product
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Inventory Value
                        </CardTitle>
                        <BarChart3 className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            ${stats?.total_inventory_value || '0.00'}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Total inventory value
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Low Stock</CardTitle>
                        <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-orange-600">
                            {stats?.low_stock_products || 0}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {stats?.out_of_stock_products || 0} out of stock
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Main Content Tabs */}
            <Tabs defaultValue="overview" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="alerts">Alerts</TabsTrigger>
                    <TabsTrigger value="reports">Reports</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                        {/* Low Stock Products */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <AlertTriangle className="h-5 w-5 text-orange-500" />
                                    Low Stock Products
                                </CardTitle>
                                <CardDescription>Products that need restocking</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {overview?.low_stock_products?.map(product => (
                                        <div
                                            key={product.product_id}
                                            className="flex items-center justify-between p-3 border rounded-lg"
                                        >
                                            <div className="flex-1">
                                                <h4 className="font-medium">{product.product_name}</h4>
                                                <p className="text-sm text-muted-foreground">
                                                    SKU: {product.sku}
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    Supplier: {product.supplier}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline" className="text-orange-600">
                                                    {product.stock_quantity} units
                                                </Badge>
                                                <div className="flex gap-1">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() =>
                                                            handleViewProductDetails(product.product_id)
                                                        }
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        onClick={() =>
                                                            handleAdjustInventory(product.product_id)
                                                        }
                                                    >
                                                        <Plus className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {(!overview?.low_stock_products ||
                                        overview.low_stock_products.length === 0) && (
                                            <p className="text-muted-foreground text-center py-4">
                                                No low stock products
                                            </p>
                                        )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Out of Stock Products */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Minus className="h-5 w-5 text-red-500" />
                                    Out of Stock Products
                                </CardTitle>
                                <CardDescription>Products with zero inventory</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {overview?.out_of_stock_products?.map(product => (
                                        <div
                                            key={product.product_id}
                                            className="flex items-center justify-between p-3 border rounded-lg"
                                        >
                                            <div className="flex-1">
                                                <h4 className="font-medium">{product.product_name}</h4>
                                                <p className="text-sm text-muted-foreground">
                                                    SKU: {product.sku}
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    Supplier: {product.supplier}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge variant="destructive">Out of Stock</Badge>
                                                <div className="flex gap-1">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() =>
                                                            handleViewProductDetails(product.product_id)
                                                        }
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        onClick={() =>
                                                            handleAdjustInventory(product.product_id)
                                                        }
                                                    >
                                                        <Plus className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {(!overview?.out_of_stock_products ||
                                        overview.out_of_stock_products.length === 0) && (
                                            <p className="text-muted-foreground text-center py-4">
                                                No out of stock products
                                            </p>
                                        )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Recent Movements */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <TrendingUp className="h-5 w-5" />
                                Recent Inventory Movements
                            </CardTitle>
                            <CardDescription>
                                Latest inventory adjustments and transactions
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {overview?.recent_movements?.map(movement => (
                                    <div
                                        key={movement.log_id}
                                        className="flex items-center justify-between p-3 border rounded-lg"
                                    >
                                        <div className="flex-1">
                                            <h4 className="font-medium">{movement.product_name}</h4>
                                            <p className="text-sm text-muted-foreground">
                                                SKU: {movement.sku}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                {movement.notes}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge
                                                variant={
                                                    movement.quantity_change > 0 ? 'default' : 'secondary'
                                                }
                                            >
                                                {movement.quantity_change > 0 ? '+' : ''}
                                                {movement.quantity_change}
                                            </Badge>
                                            <span className="text-sm text-muted-foreground">
                                                {movement.previous_stock} → {movement.new_stock}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                {new Date(movement.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                                {(!overview?.recent_movements ||
                                    overview.recent_movements.length === 0) && (
                                        <p className="text-muted-foreground text-center py-4">
                                            No recent movements
                                        </p>
                                    )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="alerts">
                    <InventoryAlerts />
                </TabsContent>

                <TabsContent value="reports">
                    <InventoryReports />
                </TabsContent>
            </Tabs>

            {/* Dialogs */}
            {showAdjustmentDialog && adjustmentProductId && (
                <InventoryAdjustmentDialog
                    productId={adjustmentProductId}
                    onSuccess={() => {
                        setShowAdjustmentDialog(false);
                        setAdjustmentProductId(null);
                    }}
                    onCancel={() => {
                        setShowAdjustmentDialog(false);
                        setAdjustmentProductId(null);
                    }}
                />
            )}

            {showBulkAdjustmentDialog && (
                <BulkAdjustmentDialog
                    onSuccess={() => setShowBulkAdjustmentDialog(false)}
                    onCancel={() => setShowBulkAdjustmentDialog(false)}
                />
            )}

            {selectedProductId && (
                <ProductInventoryDetails
                    productId={selectedProductId}
                    onClose={() => setSelectedProductId(null)}
                />
            )}
        </div>
    );
};

// Inventory Alerts Component
const InventoryAlerts: React.FC = () => {
    const { useGetInventoryAlertsQuery } = useInventorySlice();

    const {
        data: alerts,
        isLoading,
        error,
    } = useGetInventoryAlertsQuery({ threshold: 10 });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                    Failed to load inventory alerts. Please try again later.
                </AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="space-y-4">
            {/* Alert Summary */}
            <div className="grid gap-4 md:grid-cols-4">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Total Alerts</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {alerts?.summary?.total_alerts || 0}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Low Stock</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-orange-600">
                            {alerts?.summary?.low_stock_count || 0}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Out of Stock</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-red-600">
                            {alerts?.summary?.out_of_stock_count || 0}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">
                            High Value Low Stock
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-purple-600">
                            {alerts?.summary?.high_value_low_stock_count || 0}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Alert List */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Bell className="h-5 w-5" />
                        Inventory Alerts
                    </CardTitle>
                    <CardDescription>
                        Products requiring immediate attention
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-3">
                        {alerts?.alerts?.map(alert => (
                            <Alert
                                key={`${alert.product_id}-${alert.alert_type}`}
                                variant={
                                    alert.alert_type === 'out_of_stock'
                                        ? 'destructive'
                                        : 'default'
                                }
                            >
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <strong>{alert.product_name}</strong> - {alert.message}
                                            <p className="text-sm text-muted-foreground mt-1">
                                                SKU: {alert.sku} | Supplier: {alert.supplier}
                                            </p>
                                        </div>
                                        <Badge
                                            variant={
                                                alert.alert_type === 'out_of_stock'
                                                    ? 'destructive'
                                                    : alert.alert_type === 'low_stock'
                                                        ? 'secondary'
                                                        : 'default'
                                            }
                                        >
                                            {alert.alert_type.replace('_', ' ')}
                                        </Badge>
                                    </div>
                                </AlertDescription>
                            </Alert>
                        ))}
                        {(!alerts?.alerts || alerts.alerts.length === 0) && (
                            <p className="text-muted-foreground text-center py-4">
                                No alerts at this time
                            </p>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default InventoryManagement;
